<script lang="ts">
	import { orderFilterProducts, type ProductsEndpoint } from '$lib/js/products'
	import { is_set_array, setSearchParam } from '$lib/js/helpers'
	import { storeActiveFilters, storeSearchFilters } from '$lib/js/stores'
	import { onMount } from 'svelte'
	import { page } from '$app/stores'
	import { t } from 'svelte-i18n-lingui'
	import { getCheckedBrandsIDs } from '$lib/js/search'
	import type { BaseBrand, BaseProduct, BrandLink, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import '$lib/css/autocomplete.scss'
	import { getContextScope } from '$lib/js/helpers/i18n'

	export let options: ProductQueryOptions
	export let products: (BaseProduct | FullProduct)[] = []
	export let type: string = '' // '' | search
	export let endpoint: ProductsEndpoint

	let brands: (BaseBrand & BrandLink)[] = type === 'search' ? $storeSearchFilters?.brands : $page.data.brands
	let brand_aggs = type === 'search' ? $storeSearchFilters?.brand_aggs : $page.data.brand_aggs
	const scope = getContextScope()

	let displayedBrands: (BaseBrand & BrandLink)[] = brands.slice(0, 20)
	let displayAll: boolean = false

	/* Autocomplete variables */
	$: inputBrand = ''

	async function searchItems(input: string) {
		if (input.length <= 0) {
			return []
		}

		brands.sort((a, b) => {
			return a.page_title < b.page_title ? -1 : 1
		})

		// Return a list of matches.
		displayedBrands = brands.filter((item) => item.page_title.toLocaleLowerCase().includes(input.toLocaleLowerCase()))
		return []
	}

	// Functions for displaying more/fewer brands and ordering
	function showAllBrands() {
		displayedBrands = brands.sort((a, b) => {
			return a.page_title < b.page_title ? -1 : 1
		})
		displayAll = true
	}

	function showLessBrands() {
		displayedBrands = [...brands].sort((a, b) =>
			brand_aggs.indexOf(parseInt(a.option_id)) - brand_aggs.indexOf(parseInt(b.option_id)),
		).slice(0, 20)
		displayAll = false
	}

	$: if (!inputBrand || inputBrand === '') {
		displayedBrands = [...brands].sort((a, b) =>
			brand_aggs.indexOf(parseInt(a.option_id)) - brand_aggs.indexOf(parseInt(b.option_id)),
		).slice(0, 20)
	}

	async function orderFilteredProducts() {
		options.start = 0
		$storeActiveFilters.brands = displayedBrands.filter((brand) => brand.checked == true)
		options.brand_ids = getCheckedBrandsIDs(displayedBrands)

		products = await orderFilterProducts(options, endpoint, { scope: scope })
		updateUrl()

		options.start = 50
		document.getElementById('top-page')?.scrollIntoView({ behavior: 'smooth' })
	}

	function updateUrl() {
		if (type !== 'search') {
			const selectedBrandSlugs = $storeActiveFilters.brands?.map((brand) => brand.slug).join(',')
			setSearchParam('brands', selectedBrandSlugs)
		}
	}

	// Clean checked brands on clear globally
	if (!is_set_array($storeActiveFilters?.brands)) {
		displayedBrands.map((brand) => (brand.checked = false))
	}

	onMount(() => {
		let brands_slugs_list = $page.url.searchParams.get('brands')?.split(',')

		if (brands_slugs_list && type !== 'search') {
			displayedBrands.map((brand) => {
				if (brands_slugs_list.includes(brand.slug)) {
					brand.checked = true
				}
			})
			$storeActiveFilters.brands = displayedBrands.filter((brand) => brand.checked === true)
		}
	})
</script>

{#key $storeActiveFilters?.brands}
	{#if brands && brands.length > 0}
		<div class="brand-filters">
			<div class="u-autocomplete brands">
				<input
					type="text"
					on:input={() => searchItems(inputBrand)}
					bind:value={inputBrand}
					placeholder={$t`Search brands...`}
				>

				{#if inputBrand && inputBrand.length > 0}
					<button class="clear" on:click={() => (inputBrand = '')}>
						<span class="icon icon-cross" />
					</button>
				{/if}
			</div>

			<ul class="brand-filters__list">
				{#if is_set_array(displayedBrands)}
					{#each displayedBrands as brand}
						<li class:checked={brand.checked === true}>
							<input type="checkbox" id={brand.option_id}
										 bind:checked={brand.checked}
										 on:change={() => { orderFilteredProducts()}}
							/>
							<label class="brand-filters__brand" for={brand.option_id}>
								<a class='a-no-style'
									 on:click={(e)=>{brand.checked = !brand.checked; orderFilteredProducts(); e.preventDefault()}}
									 href={`${$page.url.pathname}/b/${brand.slug}`}>
									{brand.page_title}
								</a>
							</label>
						</li>
					{/each}
				{:else}
					<p class="no-results no-results__search">{$t`No brands to display`}</p>
				{/if}

				{#if inputBrand === '' && brands.length > 20}
					{#if displayAll === false || displayedBrands.length === 20}
						<button on:click={() => showAllBrands()} class="all-brands all">
							{$t`View all brands`}
						</button>
					{:else}
						<button on:click={() => showLessBrands()} class="all-brands less">
							{$t`View less brands`}
						</button>
					{/if}
				{/if}
			</ul>
		</div>
	{:else}
		<p class="no-results">{$t`No brands found`}</p>
	{/if}
{/key}

<style lang="scss">
  .a-no-style {
    color: inherit;
    text-decoration: inherit;
  }

  .no-results {
    color: var(--grey--b);

    &__search {
      margin: 4px;
      border: 1px solid var(--grey);
      border-radius: 5px;
    }
  }

  .clear {
    position: absolute;
    top: 8px;
    right: 10px;

    .icon-cross {
      color: var(--main);
      margin: -10px;
      padding: 10px;
    }
  }

  .brand-filters {
    .all-brands {
      color: black;
      display: block;
      margin-top: 8px;
      margin-left: 4px;

      &:hover {
        color: var(--main);
      }
    }

    &__list {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      background-color: var(--grey);
      border-radius: 5px;
      max-height: 130px;
      padding: 7px;
      overflow-y: auto;

      li {
        display: flex;
        column-gap: 2px;

        &:hover {
          color: var(--main);
        }

        &.checked {
          order: -1;
        }

        label {
          flex-grow: 1;
        }
      }
    }

    .u-autocomplete {
      position: relative;
      margin-bottom: 10px;
      border-radius: 5px;
      background-color: var(--grey);
      color: var(--grey--b);

      input {
        border: none;
        background: none;
        padding: 7px 10px;

        &:focus {
          outline: none;
        }
      }
    }
  }
</style>
